/**
 * GEO 拓展关键词模型(R2)—— draft.keywordsJson 的唯一解析/序列化层。
 *
 * 形状真源:api xb_reporter_draft.keywords_json / ai keyword_expand.py
 *   [{word, group: brand|industry|question|region, source: manual|expand|geo_radar, selected}]
 * 口径:词只是表达覆盖目标不是事实依据(ai lexicon.KEYWORD_USAGE_RULES);
 * 拓词产出=候选,勾选状态由用户改、随 draft 保存;source 用于合并语义
 * (后端 KEYWORD_EXPAND 回调只换 expand 批,manual/geo_radar 不被覆盖)。
 */

export type KeywordGroup = "brand" | "industry" | "question" | "region";
export type KeywordSource = "manual" | "expand" | "geo_radar";

export interface KeywordItem {
  word: string;
  group: KeywordGroup;
  source: KeywordSource;
  selected: boolean;
}

export const KEYWORD_GROUP_LABEL: Record<KeywordGroup, string> = {
  question: "问答长尾",
  industry: "行业词",
  brand: "品牌词",
  region: "地域词",
};

/** 展示顺序:问答长尾(GEO 核心)在前。 */
export const KEYWORD_GROUP_ORDER: KeywordGroup[] = ["question", "industry", "brand", "region"];

const GROUPS = new Set<string>(KEYWORD_GROUP_ORDER);
const SOURCES = new Set<string>(["manual", "expand", "geo_radar"]);

/** 容错解析:坏 JSON/坏条目按空;坏组归 industry、坏来源归 manual(与后端宽进口径一致)。 */
export function parseKeywords(raw: string | null | undefined): KeywordItem[] {
  if (!raw) return [];
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(data)) return [];
  const out: KeywordItem[] = [];
  const seen = new Set<string>();
  for (const item of data) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const word = typeof o.word === "string" ? o.word.trim() : "";
    if (!word || seen.has(word)) continue;
    seen.add(word);
    out.push({
      word,
      group: GROUPS.has(String(o.group)) ? (String(o.group) as KeywordGroup) : "industry",
      source: SOURCES.has(String(o.source)) ? (String(o.source) as KeywordSource) : "manual",
      selected: o.selected !== false,
    });
  }
  return out;
}

export function serializeKeywords(items: KeywordItem[]): string {
  return JSON.stringify(items);
}

export function selectedWords(items: KeywordItem[]): string[] {
  return items.filter((k) => k.selected).map((k) => k.word);
}
